import React, { useState, useMemo } from 'react';
import {
  Table,
  Input,
  Modal,
  Select,
  Tooltip,
  Dropdown,
  Menu,
  Tag,
  DatePicker,
  Popover,
  Typography,
} from 'antd';
import { Resizable } from 'react-resizable';
import { AiOutlineDown, AiOutlineRight, AiOutlinePlus } from 'react-icons/ai';
import InlineCreateRow from './InlineCreateRow';
import 'react-resizable/css/styles.css';
import { ThunderboltOutlined } from '@ant-design/icons';
import TaskObject from '@atlaskit/object/task';
import StoryObject from '@atlaskit/object/story';
import BugObject from '@atlaskit/object/bug';
import SubtaskObject from '@atlaskit/object/subtask';
import dayjs from 'dayjs';
import './Table.css';
import TaskModal from './ListTaskModal';
import Cookies from 'js-cookie';
import UserAvatar from '../../../../components/userAvatar/index';
import SubTaskModal from '../Board/SubTaskModal';
import ListEpicModal from './ListEpicModal';
import httpInjectorService from 'services/http-injector.service';

const { Option } = Select;
const { Title, Text } = Typography;

const ResizableTitle = (props) => {
  const { onResize, width, ...rest } = props;
  if (!width) return <th {...rest} />;

  return (
    <Resizable
      width={width}
      height={0}
      handle={<span className="react-resizable-handle" />}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...rest} />
    </Resizable>
  );
};

const TableUI = ({
  epics,
  toggleEpic,
  toggleTask,
  isEditing,
  setEditingCell,
  handleCellSave,
  assigneeOptions,
  getListData,
  epicOptions,
  taskDetails,
  statusOptions,
  sprintOptions,
  labelOptions,
  projectId,
  subtaskDetails,
  epicDetails,
  isFiltered,
  searchTerm,
  selectedTypes,
  selectedStatuses,
  selectedAssignees,
  issueTypes,
}) => {
  const [inlineInput, setInlineInput] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [subtaskmodal, setSubtaskmodal] = useState(false);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [epicmodalvisible, setEpicModalVisible] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [showChildInput, setShowChildInput] = useState(false);

  const roleId = Number(Cookies.get('task-role-id'));

  const ISSUE_TYPE_ICON_MAP = {
    1: TaskObject, // TASK
    2: StoryObject, // STORY
    3: BugObject, // BUG
  };

  const getIssueIcon = (issueTypeId, size = 35) => {
    const Comp = ISSUE_TYPE_ICON_MAP[String(issueTypeId)] || TaskObject;
    return <Comp width={size} />;
  };

  const getStatusColors = (status = '') => {
    const s = String(status || '')
      .toUpperCase()
      .trim();

    switch (s) {
      case 'DONE':
        return { bg: '#45d44fff', color: 'black' };
      case 'TO DO':
      case 'TODO':
        return { bg: '#E6E6E6', color: 'black' };
      default:
        return { bg: '#55b0e8ff', color: 'black' };
    }
  };

  const StatusSelectComponent = ({ record }) => (
    <Select
      size="small"
      autoFocus
      value={record.status}
      style={{ width: 110 }}
      getPopupContainer={() => document.body}
      onChange={(val) => {
        handleCellSave(record, 'status', val);
      }}
      onBlur={() => {
        setEditingCell({ rowId: null, field: null });
      }}
    >
      {statusOptions.map((o) => (
        <Option key={o.status_id} value={o.status_name}>
          {o.status_name}
        </Option>
      ))}
    </Select>
  );

  const SprintSelectComponent = ({ record }) => (
    <Select
      size="small"
      defaultValue={record.sprint_name}
      autoFocus
      getPopupContainer={() => document.body}
      onChange={(val) => handleCellSave(record, 'sprint', val)}
      style={{ width: 110 }}
    >
      {sprintOptions.map((o) => (
        <Select.Option key={o.value} value={o.label}>
          {o.label}
        </Select.Option>
      ))}
    </Select>
  );

  const assigneeMenu = (record) => (
    <Menu
      onClick={({ key }) => {
        const user = assigneeOptions.find(
          (u) => String(u.user_id) === String(key),
        );
        if (!user) return;

        handleCellSave(record, 'assignee', user.username);
      }}
    >
      {assigneeOptions.map((u) => (
        <Menu.Item key={u.user_id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAvatar username={u.username} size={24} />
            <span>{u.username}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const safeUsername = (username) =>
    typeof username === 'string' && username.trim() !== '' ? username : '?';

  const normalizeLabels = (labels) => {
    if (!labels) return [];

    // Case 1: backend sends string "[a, b, c]"
    if (typeof labels === 'string') {
      try {
        return labels
          .replace(/[\[\]]/g, '')
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((name) => ({
            label_name: name,
            label_id: name, // temporary stable key
          }));
      } catch {
        return [];
      }
    }

    // Case 2: backend sends array
    if (Array.isArray(labels)) {
      return labels.map((l) => ({
        label_id: l.label_id || l.value,
        label_name: l.label_name || l.label,
      }));
    }

    return [];
  };

  const getIssueTypeName = (issue_type_id) => {
  switch (issue_type_id) {
    case 1:
      return "Task";
    case 2:
      return "Story";
    case 3:
      return "Bug";
    default:
      return "Task";
  }
};


  const [columns, setColumns] = useState([
    {
      title: '',
      width: 50,
      render: (_, record) =>
        record.isInline ? (
          {
            children: record.inlineNode,
            props: { colSpan: 12 },
          }
        ) : (
          <input type="checkbox" />
        ),
    },
    { title: 'Type', dataIndex: 'type', width: 140 },
    {
      title: 'Key',
      dataIndex: 'keyColumn',
      width: 120,
      onCell: (record) => ({
        onClick: () => {
          if (record.rowType === 'issue') {
            handleOpenModal({ record, projectId });
          }
          if (record.rowType === 'subtask') {
            handleOpenSubtaskModal({ record, projectId });
          }
          if (record.rowType === 'epic') {
            handleOpenEpicModal({ record, projectId });
          }
        },

        style: {
          cursor: 'pointer',
          color: '#1890ff',
          textDecoration: 'underline',
        },
      }),
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      width: 350,
      render: (val, record) =>
        isEditing(record, 'summary') ? (
          <Input
            size="small"
            autoFocus
            defaultValue={val}
            onPressEnter={(e) =>
              handleCellSave(record, 'summary', e.target.value)
            }
            // onBlur={(e) => handleCellSave(record, 'summary', e.target.value)}
          />
        ) : (
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditingCell({ rowId: record.rowId, field: 'summary' });
            }}
            style={{ cursor: 'pointer' }}
          >
            {val}
          </div>
        ),
    },
    {
      title: 'Status',
      width: 120,
      render: (_, record) => {
        const statusValue = record.status?.status_name || record.status;

        const colors = getStatusColors(statusValue);

        return isEditing(record, 'status') ? (
          <StatusSelectComponent record={record} />
        ) : (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu
                onClick={({ key }) => {
                  const selectedStatus = statusOptions.find(
                    (s) => String(s.id) === String(key),
                  );
                  if (!selectedStatus) return;

                  handleCellSave(record, 'status', selectedStatus.status_name);
                }}
              >
                {statusOptions.map((s) => {
                  const c = getStatusColors(s.status_name);
                  return (
                    <Menu.Item
                      key={s.id}
                      style={{
                        backgroundColor: c.bg,
                        color: c.color,
                        fontWeight: 500,
                        borderRadius: 6,
                        margin: '4px 0',
                      }}
                    >
                      {s.status_name}
                    </Menu.Item>
                  );
                })}
              </Menu>
            }
          >
            <Tooltip title={statusValue || 'Select Status'} placement="top">
              <Tag
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: colors.bg,
                  color: colors.color,
                }}
              >
                {statusValue || ''}
              </Tag>
            </Tooltip>
          </Dropdown>
        );
      },
    },
    {
      title: 'Sprint',
      dataIndex: 'sprint_name',
      width: 120,
      render: (_, record) =>
        isEditing(record, 'sprint') ? (
          <SprintSelectComponent record={record} />
        ) : (
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditingCell({ rowId: record.rowId, field: 'sprint' });
            }}
            style={{ cursor: 'pointer' }}
          >
            {record.sprint_name || '—'}
          </div>
        ),
    },
    {
      title: 'Assignee',
      width: 180,
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          overlay={assigneeMenu(record)}
          getPopupContainer={() => document.body}
        >
         <div
  onClick={(e) => e.stopPropagation()}
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
  }}
>
  <Tooltip title={safeUsername(record.assignee)}>
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <UserAvatar
        username={safeUsername(record.assignee)}
        size={24}
        className="clickable-avatar"
      />
      <span style={{ whiteSpace: "nowrap" }}>
        {safeUsername(record.assignee)}
      </span>
    </div>
  </Tooltip>
</div>

        </Dropdown>
      ),
    },
    {
      title: 'Due Date',
      width: 120,
      render: (_, record) => (
        <Popover
          trigger="click"
          content={
            <DatePicker
              autoFocus
              value={record.dueDate ? dayjs(record.dueDate) : null}
              format="DD MMM"
              onChange={(date) =>
                handleCellSave(
                  record,
                  'dueDate',
                  date ? date.format('YYYY-MM-DD') : null,
                )
              }
            />
          }
        >
          <Tag
            style={{
              width: '100%',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            {record.dueDate ? dayjs(record.dueDate).format('DD MMM') : '—'}
          </Tag>
        </Popover>
      ),
    },
    {
      title: 'Labels',
      width: 180,
      render: (_, record) => {
        const labels = Array.isArray(record.labels)
          ? record.labels.filter((l) => l?.label_id || l?.value)
          : [];

        return (
          <Popover
            trigger="click"
            placement="bottomLeft"
            overlayInnerStyle={{
              width: 240,
              maxHeight: 260,
              overflowY: 'auto',
              padding: 6,
            }}
            content={
              labelOptions.length ? (
                labelOptions.map((l) => {
                  const isSelected = labels.some(
                    (sel) => sel?.label_id === l.label_id,
                  );

                  return (
                    <div
                      key={l.label_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 10px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: isSelected ? '#e6f4ff' : 'transparent',
                      }}
                      onClick={() => {
                        const updatedLabels = isSelected
                          ? labels.filter((sel) => sel.label_id !== l.label_id)
                          : [...labels, l];

                        handleCellSave(record, 'labels', updatedLabels);
                      }}
                    >
                      <Tag key={l.label_id || l.value}>
                        {l.label_name || l.label}
                      </Tag>
                    </div>
                  );
                })
              ) : (
                <Text type="secondary">No labels found</Text>
              )
            }
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                cursor: 'pointer',
              }}
            >
              {labels.length ? (
                labels.map((l) =>
                  l?.label_id ? (
                    <Tag
                      key={l.label_id}
                      color="blue"
                      style={{ fontSize: 12, margin: 0 }}
                    >
                      {l.label_name}
                    </Tag>
                  ) : null,
                )
              ) : (
                <Text type="secondary">Add labels</Text>
              )}
            </div>
          </Popover>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      width: 120,
    },
    {
      title: 'Updated',
      dataIndex: 'updated',
      width: 120,
    },
    {
      title: 'Reporter',
      width: 180,
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <UserAvatar username={safeUsername(record.reporter)} size={24} />
          <span
            style={{
              fontSize: 13,
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {safeUsername(record.reporter)}
          </span>
        </div>
      ),
    },
  ]);

  const handleOpenEpicModal = ({ record, projectId }) => {
    if (!record || record.rowType !== 'epic') return;
    setSelectedEpic({
      epic_id: record.entityId,
      project_id: projectId,
      epic_key: record.keyColumn,
      title: record.summary,
      status: record.status,
      assignee: record.assignee,
      due_date: record.dueDate,
      labels: record.labels,
    });
    setEpicModalVisible(true);
  };

  const handleCloseEpicModal = () => {
    setEpicModalVisible(false);
    setSelectedEpic(null);
  };

  const handleOpenModal = ({ record, projectId }) => {
    if (!record || record.rowType !== 'issue') return;

    setSelectedTask({
      issue_id: record.entityId,
      project_id: projectId,
      issue_key: record.keyColumn,
      issue_type_id: record.issue_type_id,
      issue_title: record.summary,
      issue_status: record.status,
      assingee_name: record.assignee,
      due_date: record.dueDate,
      labels: record.labels,
    });

    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedTask(null);
  };

  const handleOpenSubtaskModal = ({ record, projectId }) => {
    if (!record || record.rowType !== 'subtask') return;
    setSelectedSubtask({
      subtask_id: record.entityId,
      issue_id: record.issue_id,
      project_id: projectId,
      subtask_key: record.keyColumn,
      title: record.summary,
      status: record.status,
      assignee: record.assignee,
      due_date: record.dueDate,
      labels: record.labels,
    });
    setSubtaskmodal(true);
  };

  const handleclosesubtaskmodal = () => {
    setSubtaskmodal(false);
    setSelectedSubtask(null);
  };

  const handleResize =
    (index) =>
    (_, { size }) => {
      setColumns((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], width: size.width };
        return next;
      });
    };

  const mergedColumns = columns.map((col, index) =>
    col.width
      ? {
          ...col,
          onHeaderCell: () => ({
            width: col.width,
            onResize: handleResize(index),
          }),
        }
      : col,
  );

  const handleAddTask = async (data) => {
    if (!data || !data.issue_title?.trim()) return;

    const reqbody = {
      project_id: projectId,
      epic_id: inlineInput.epicId,
      issue_type_id: data.issue_type_id,
      issue_title: data.issue_title,
      issue_status: 'TO DO',
      subtask_priority: 'Medium',
      start_date: null,
      due_date: null,
      labels_id: null,
      subtask_assignee_id: null,
      description: null,
    };

    try {
      const res = await httpInjectorService.addtaskwithEpic(reqbody);
      if (res.status === 'success') {
        setTaskTitle('');
        setInlineInput(null);
        getListData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddSubtask = async (data) => {
    if (!data || !data.issue_title?.trim()) return;

    const reqbody = {
      project_id: projectId,
      issue_id: inlineInput.taskId,
      title: data.issue_title,
      subtask_status: 'TO DO',
      issue_type_id: inlineInput.parentIssueTypeId,
      issue_type: inlineInput.parentIssueType,
    };

    try {
      const res = await httpInjectorService.addSubtask(reqbody);
      if (res.status === 'success') {
        setSubtaskTitle('');
        setInlineInput(null);
        getListData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  let tableData = [];

  epics.forEach((e) => {
    tableData.push({
      key: `${e.epic_id}`,
      rowId: `${e.epic_id}`,
      rowType: 'epic',
      entityId: e.epic_id,
      keyColumn: e.epic_key,
      summary: e.epic_name,
      status: e.epic_status,
      sprint_name: e.sprint_name,
      assignee: e.assignee_name,
      dueDate: e.due_date,
      labels: normalizeLabels(e.labels),

      created: e.created_at ? dayjs(e.created_at).format('DD MMM YYYY') : '—',
      updated: e.updated_at ? dayjs(e.updated_at).format('DD MMM YYYY') : '—',
      reporter: e.reporter_name || '—',
      type: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {e.tasks?.length ? (
            e.isExpanded ? (
              <AiOutlineDown onClick={() => toggleEpic(e.epic_id)} />
            ) : (
              <AiOutlineRight onClick={() => toggleEpic(e.epic_id)} />
            )
          ) : (
            <AiOutlineRight style={{ visibility: 'hidden' }} />
          )}
          <ThunderboltOutlined style={{ marginLeft: 6, color: '#884B9E' }} />
          <AiOutlinePlus
            style={{ marginLeft: 10 }}
            onClick={() =>
              setInlineInput({
                type: 'issue',
                epicId: e.epic_id,
                value: '',
              })
            }
          />
        </div>
      ),
    });

    if (
      inlineInput &&
      inlineInput.type === 'issue' &&
      inlineInput.epicId === e.epic_id
    ) {
      tableData.push({
        key: `inline-task-${e.epic_id}`,
        isInline: true,
        inlineNode: (
          <InlineCreateRow
            placeholder="What needs to be done"
            onCancel={() => setInlineInput(null)}
            createLevel="issue"
            onCreate={handleAddTask}
            onChange={setTaskTitle}
          />
        ),
      });
    }

    if (e.isExpanded) {
      e.tasks.forEach((t) => {
        tableData.push({
          key: `${t.issue_id}`,
          rowId: `${t.issue_id}`,
          rowType: 'issue',
          entityId: t.issue_id,
          issue_type_id: t.issue_type_id,
          keyColumn: t.issue_key,
          summary: t.issue_title,
          status: t.issue_status,
          sprint_name: t.sprint_name,
          assignee: t.assignee_name,
          dueDate: t.due_date,
          labels: normalizeLabels(t.labels),

          created: t.created_at
            ? dayjs(t.created_at).format('DD MMM YYYY')
            : '—',
          updated: t.updated_at
            ? dayjs(t.updated_at).format('DD MMM YYYY')
            : '—',
          reporter: t.reporter_name || '—',
          type: (
            <div
              style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}
            >
              {t.subtasks?.length ? (
                t.isExpanded ? (
                  <AiOutlineDown
                    onClick={() => toggleTask(e.epic_id, t.issue_id)}
                  />
                ) : (
                  <AiOutlineRight
                    onClick={() => toggleTask(e.epic_id, t.issue_id)}
                  />
                )
              ) : (
                <AiOutlineRight style={{ visibility: 'hidden' }} />
              )}

              {getIssueIcon(t.issue_type_id)}

              <AiOutlinePlus
                style={{ marginLeft: 10 }}
                onClick={() =>
                  setInlineInput({
                    type: 'subtask',
                    taskId: t.issue_id,
                    parentIssueTypeId: t.issue_type_id,
                    parentIssueType: getIssueTypeName(t.issue_type_id),
                    value: '',
                  })
                }
              />
            </div>
          ),
        });

        if (
          inlineInput &&
          inlineInput.type === 'subtask' &&
          inlineInput.taskId === t.issue_id
        ) {
          tableData.push({
            key: `inline-subtask-${t.issue_id}`,
            isInline: true,
            inlineNode: (
              <InlineCreateRow
                placeholder="Add a subtask"
                onCancel={() => setInlineInput(null)}
                createLevel="subtask"
                onChange={setSubtaskTitle}
                onCreate={handleAddSubtask}
              />
            ),
          });
        }

        if (t.isExpanded) {
          t.subtasks.forEach((s) => {
            tableData.push({
              key: `${s.subtask_id}`,
              rowId: `${s.subtask_id}`,
              rowType: 'subtask',
              entityId: s.subtask_id,
              keyColumn: s.subtask_key,
              summary: s.title,
              status: s.subtask_status,
              sprint_name: s.sprint_name,
              assignee: s.assignee_name,
              dueDate: s.due_date,
              labels: normalizeLabels(s.labels),

              created: s.created_at
                ? dayjs(s.created_at).format('DD MMM YYYY')
                : '—',
              updated: s.updated_at
                ? dayjs(s.updated_at).format('DD MMM YYYY')
                : '—',
              reporter: s.reporter_name || '—',
              type: (
                <div style={{ marginLeft: 50 }}>
                  <SubtaskObject width={28} />
                </div>
              ),
            });
          });
        }
      });
    }
  });

  if (isFiltered && selectedTypes && selectedTypes.length > 0) {
    const normalized = selectedTypes.map((t) => String(t).toLowerCase());
    const includeEpic = normalized.includes('epic');
    const includeSubtask = normalized.includes('subtask');
    const issueCodes = normalized.filter((t) => ['1', '2', '3'].includes(t));

    if (includeEpic || includeSubtask || issueCodes.length > 0) {
      tableData = tableData.filter((row) => {
        if (includeEpic && row.rowType === 'epic') return true;
        if (includeSubtask && row.rowType === 'subtask') return true;
        if (issueCodes.length > 0 && row.rowType === 'issue') {
          return issueCodes.includes(String(row.issue_type_id));
        }
        return false;
      });

      // Update the type column visuals for the filtered rows
      tableData.forEach((row) => {
        if (row.rowType === 'epic' && includeEpic) {
          row.type = (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ThunderboltOutlined
                style={{ marginLeft: 6, color: '#884B9E' }}
              />
            </div>
          );
        }

        if (row.rowType === 'subtask' && includeSubtask) {
          row.type = (
            <div>
              <SubtaskObject width={28} />
            </div>
          );
        }

        if (row.rowType === 'issue' && issueCodes.length > 0) {
          row.type = (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {getIssueIcon(row.issue_type_id)}
            </div>
          );
        }
      });
    }
  }

  if (isFiltered && selectedStatuses.length > 0) {
    tableData = tableData.filter((row) =>
      selectedStatuses.includes(row.status),
    );
  }

  if (isFiltered && selectedAssignees.length > 0) {
    const assigneeUsernames = assigneeOptions
      .filter((a) => selectedAssignees.includes(a.user_id))
      .map((a) => a.username);
    tableData = tableData.filter((row) =>
      assigneeUsernames.includes(row.assignee),
    );
  }

  if (searchTerm) {
    tableData = tableData.filter(
      (row) =>
        row.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.keyColumn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.assignee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.reporter?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.status?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  return (
    <>
      <Table
        rowKey="rowId"
        columns={mergedColumns}
        components={{ header: { cell: ResizableTitle } }}
        dataSource={tableData}
        pagination={false}
        bordered
        tableLayout="fixed"
        scroll={{
          x: 'max-content',
          y: 'calc(100vh - 260px)',
        }}
      />

      {modalVisible && selectedTask && [1, 2, 3, 4, 5].includes(roleId) && (
        <TaskModal
          isOpen={modalVisible}
          taskDetails={selectedTask}
          onClose={handleCloseModal}
          assigneeList={assigneeOptions}
          statusList={statusOptions}
          EpicList={epicOptions}
          getTaskBoardDetails={getListData}
          tasks={taskDetails}
          handleOpenSubtaskModal={handleOpenSubtaskModal}
        />
      )}

      {subtaskmodal && selectedSubtask && (
        <SubTaskModal
          isOpen={subtaskmodal}
          subtaskdetails={selectedSubtask}
          onClose={handleclosesubtaskmodal}
          taskDetails={selectedTask}
          assigneeList={assigneeOptions}
        />
      )}

      {epicmodalvisible && selectedEpic && (
        <ListEpicModal
          isOpen={epicmodalvisible}
          epicDetails={selectedEpic}
          onClose={handleCloseEpicModal}
          projectId={projectId}
          taskDetails={selectedTask}
          issueTypes={issueTypes}
        />
      )}
    </>
  );
};

export default TableUI;
